import type { ChangelogResponse, ShipstarConfig } from "./types";
import { escapeHtml, formatDateRange } from "./utils";

const CATEGORY_ICONS: Record<string, string> = {
  new: "&#10024;",       // sparkles
  improved: "&#8593;",   // up arrow
  fixed: "&#128295;",    // wrench
  breaking: "&#9888;",   // warning
};

const CATEGORY_LABELS: Record<string, string> = {
  new: "New",
  improved: "Improved",
  fixed: "Fixed",
  breaking: "Breaking",
};

export function renderWidget(
  data: ChangelogResponse,
  config: ShipstarConfig
): string {
  if (!data.entries || data.entries.length === 0) {
    return `<div class="ss-widget"><div class="ss-empty">No changelog entries available.</div></div>`;
  }

  const entries = data.entries.slice(0, config.maxItems);

  const headerHtml = data.headline
    ? `<div class="ss-header"><h3 class="ss-title">${escapeHtml(data.headline)}</h3></div>`
    : "";

  const periodHtml =
    data.period_start && data.period_end
      ? `<p class="ss-period">${escapeHtml(formatDateRange(data.period_start, data.period_end))}</p>`
      : "";

  const itemsHtml = entries
    .map((entry) => {
      const cat = entry.category || "new";
      const icon = CATEGORY_ICONS[cat] || CATEGORY_ICONS.new;
      const label = CATEGORY_LABELS[cat] || cat;

      const descHtml = entry.description
        ? `<div class="ss-item-desc">${escapeHtml(entry.description)}</div>`
        : "";

      return `<div class="ss-item">
        <div class="ss-item-icon ss-cat-${escapeHtml(cat)}">${icon}</div>
        <div class="ss-item-body">
          <div class="ss-item-title">
            ${escapeHtml(entry.title)}
            <span class="ss-item-badge ss-badge-${escapeHtml(cat)}">${escapeHtml(label)}</span>
          </div>
          ${descHtml}
        </div>
      </div>`;
    })
    .join("");

  const shipstarIcon = `<svg width="14" height="14" viewBox="0 0 195 195" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M123.215 24.3304C125.235 21.8047 129.145 21.8047 128.921 24.3304L124.507 73.9972C124.406 75.1267 125.218 75.8915 126.518 75.8915H183.666C186.572 75.8915 186.27 79.2898 183.225 80.8508L123.349 111.547C121.987 112.245 120.997 113.482 120.897 114.612L116.482 164.278C116.258 166.804 112.161 168.904 110.504 167.343L77.9123 136.648C77.1711 135.949 75.7473 135.949 74.3856 136.648L14.5092 167.343C11.4643 168.904 9.23429 166.804 11.2549 164.278L50.9888 114.612C51.8924 113.482 52.0024 112.245 51.2612 111.547L18.6698 80.8508C17.0125 79.2898 19.7312 75.8915 22.6373 75.8915H79.7856C81.0852 75.8915 82.577 75.1267 83.4806 73.9972L123.215 24.3304Z" fill="currentColor"/></svg>`;

  const subscribeHtml = config.subscribe
    ? `<div class="ss-subscribe">
    <form class="ss-subscribe-form">
      <input type="email" class="ss-subscribe-input" placeholder="your@email.com" required aria-label="Email address" />
      <button type="submit" class="ss-subscribe-btn">Subscribe</button>
    </form>
    <p class="ss-subscribe-status"></p>
  </div>`
    : "";

  const footerHtml = config.branding
    ? `<div class="ss-footer">
    <a href="https://shipstar.ai" target="_blank" rel="noopener noreferrer">${shipstarIcon} Powered by Shipstar</a>
  </div>`
    : "";

  return `<div class="ss-widget">
    ${headerHtml}
    ${periodHtml}
    <div class="ss-items">${itemsHtml}</div>
    ${subscribeHtml}
    ${footerHtml}
  </div>`;
}
